import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

import { getAccessToken } from './api'
import { getZervantBaseURL } from './getZervantBaseURL'

const APPLICATION_ID = 'billy'
const BASE_URL = getZervantBaseURL()

// service names in Zervant's SPOE
type ServiceName =
  | 'documentReports'
  | 'invoiceDelivery'
  | 'platform'
  | 'restInvoice'
  | 'restInvoiceGraphql'
  | 'settingsService'
  | 'subscription'
  | 'templates'

type ServiceToPrefixMap = {
  [serviceName in ServiceName]: string
}

type ApiMethodResponse<T> = Promise<AxiosResponse<T, any>>

interface ApiMethods {
  get: <T>(url?: string, config?: AxiosRequestConfig<any>) => ApiMethodResponse<T>
  post: <T>(url?: string, data?: any, config?: AxiosRequestConfig<any>) => ApiMethodResponse<T>
  put: <T>(url?: string, data?: any, config?: AxiosRequestConfig<any>) => ApiMethodResponse<T>
  patch: <T>(url?: string, data?: any, config?: AxiosRequestConfig<any>) => ApiMethodResponse<T>
  delete: <T>(url?: string, config?: AxiosRequestConfig<any>) => ApiMethodResponse<T>
}

type UrlBuilder = (path?: string) => string

type ServiceUrlBuilders = {
  [serviceName in ServiceName]: UrlBuilder
}

class ApiZervant {
  private instance: AxiosInstance
  private organizationId: string | null = null
  documentReports: ApiMethods
  invoiceDelivery: ApiMethods
  platform: ApiMethods
  restInvoice: ApiMethods
  restInvoiceGraphql: ApiMethods
  settingsService: ApiMethods
  subscription: ApiMethods
  templates: ApiMethods
  urls: ServiceUrlBuilders

  // list of api URL prefixes per SPOE service
  private static serviceMap: ServiceToPrefixMap = {
    documentReports: 'document-reports',
    invoiceDelivery: 'invd',
    platform: 'platform',
    restInvoice: 'rest-invoice/api',
    restInvoiceGraphql: 'rest-invoice/graphql',
    settingsService: 'settings-service',
    subscription: 'subscription',
    templates: 'templates',
  }

  constructor() {
    this.instance = axios.create({
      baseURL: BASE_URL,
    })

    this.instance.defaults.headers.common['application-id'] = APPLICATION_ID
    this.buildScopeRequestMethods()
  }

  // redefine request methods with organizationId injected in endpoint paths
  setOrganizationId = (organizationId: string | null) => {
    this.organizationId = organizationId

    // reset potentially existing cached auth header
    this.instance.defaults.headers.common.Authorization = null

    this.buildScopeRequestMethods()
  }

  buildScopeRequestMethods = () => {
    const urlBuilders: any = {}

    for (const [scope, urlPrefix] of Object.entries(ApiZervant.serviceMap)) {
      const urlPrefixWithOrganizationId = this.organizationId
        ? `organizations/${this.organizationId}/${urlPrefix}`
        : urlPrefix

      ;(this as any)[scope] = this.getScopeRequestMethods(urlPrefixWithOrganizationId)
      urlBuilders[scope] = this.getUrlBuilder(urlPrefixWithOrganizationId)
    }

    this.urls = urlBuilders
  }

  getScopeRequestMethods = (pathPrefix: string): ApiMethods => {
    return {
      get: (url = '', config?: AxiosRequestConfig<any>) => this.get(`${pathPrefix}${url}`, config),
      post: (url = '', data?: any, config?: AxiosRequestConfig<any>) => this.post(`${pathPrefix}${url}`, data, config),
      put: (url = '', data?: any, config?: AxiosRequestConfig<any>) => this.put(`${pathPrefix}${url}`, data, config),
      patch: (url = '', data?: any, config?: AxiosRequestConfig<any>) =>
        this.patch(`${pathPrefix}${url}`, data, config),
      delete: (url = '', config?: AxiosRequestConfig<any>) => this.delete(`${pathPrefix}${url}`, config),
    }
  }

  getUrlBuilder =
    (pathPrefix: string): UrlBuilder =>
    (path = '') =>
      `${BASE_URL}/${pathPrefix}${path}`

  // @todo maybe we should send some custom event from ember to set it up here:
  setAccessToken = () => {
    if (this.instance.defaults.headers.common.Authorization) {
      return
    }

    this.instance.defaults.headers.common.Authorization = `Bearer ${getAccessToken()}`
  }

  get = <T>(url: string, config?: AxiosRequestConfig) => {
    this.setAccessToken()
    return this.instance.get<T>(url, config)
  }

  post = <T>(url: string, data?: any, config?: AxiosRequestConfig<any>) => {
    this.setAccessToken()
    return this.instance.post<T>(url, data, config)
  }

  put = <T>(url: string, data?: any, config?: AxiosRequestConfig<any>) => {
    this.setAccessToken()
    return this.instance.put<T>(url, data, config)
  }

  patch = <T>(url: string, data?: any, config?: AxiosRequestConfig<any>) => {
    this.setAccessToken()
    return this.instance.patch<T>(url, data, config)
  }

  delete = <T>(url: string, config?: AxiosRequestConfig) => {
    this.setAccessToken()
    return this.instance.delete<T>(url, config)
  }
}

const api = new ApiZervant()
export default api
